import { Permission, Role, User } from '@/types/api-responses';

// Permission display helpers
export const PERMISSION_ACTION_LABELS: Record<string, string> = {
  read: 'View',
  write: 'Create',
  update: 'Edit',
  delete: 'Delete',
  manage: 'Full Access',
  approve: 'Approve',
  reject: 'Reject',
  export: 'Export',
  import: 'Import',
  bulk: 'Bulk Operations',
};

export const PERMISSION_RESOURCE_LABELS: Record<string, string> = {
  users: 'Users',
  customers: 'Customers',
  suppliers: 'Suppliers',
  inventory: 'Inventory',
  orders: 'Sales Orders',
  'purchase-orders': 'Purchase Orders',
  'purchase-requisitions': 'Purchase Requisitions',
  discounts: 'Promotions',
  regions: 'Regions',
  transactions: 'Transactions',
  reports: 'Reports',
  settings: 'System Settings',
  'audit-logs': 'Audit Logs',
  notifications: 'Notifications',
  roles: 'Role Management',
};

// Role utilities
export const getRoleDisplayName = (role: string | Role): string => {
  if (typeof role === 'string') {
    return role.charAt(0).toUpperCase() + role.slice(1);
  }
  return role.name.charAt(0).toUpperCase() + role.name.slice(1);
};

export const getRolePermissionCount = (role: Role): number => {
  if (!role.permissions) return 0;
  return role.permissions.reduce((count, permission) => {
    return count + (permission.actions?.length || 0);
  }, 0);
};

// User utilities
export const getUserRoleName = (user: User | any): string => {
  // Prefer explicit roleName when available
  if ((user as any).roleName) return (user as any).roleName;
  const role = (user as any).role;
  if (typeof role === 'string') return role;
  return role?.name || 'No Role';
};

export const transformUserPermissions = (
  permissions: Permission[]
): Record<string, string[]> => {
  return permissions.reduce(
    (acc, permission) => {
      acc[permission.resource] = permission.actions;
      return acc;
    },
    {} as Record<string, string[]>
  );
};

// Permission comparison utilities
export const comparePermissions = (
  oldPermissions: Permission[],
  newPermissions: Permission[]
): {
  added: Permission[];
  removed: Permission[];
  unchanged: Permission[];
} => {
  const oldMap = new Map(
    oldPermissions.map(p => [`${p.resource}:${p.actions.join(',')}`, p])
  );
  const newMap = new Map(
    newPermissions.map(p => [`${p.resource}:${p.actions.join(',')}`, p])
  );

  const added = newPermissions.filter(
    p => !oldMap.has(`${p.resource}:${p.actions.join(',')}`)
  );
  const removed = oldPermissions.filter(
    p => !newMap.has(`${p.resource}:${p.actions.join(',')}`)
  );
  const unchanged = newPermissions.filter(p =>
    oldMap.has(`${p.resource}:${p.actions.join(',')}`)
  );

  return { added, removed, unchanged };
};

// Bulk operation utilities
export const validateBulkOperation = (
  selectedUsers: User[],
  selectedRole?: string
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (selectedUsers.length === 0) {
    errors.push('Please select at least one user');
  }

  if (selectedUsers.length > 50) {
    errors.push('Cannot update more than 50 users at once');
  }

  if (!selectedRole) {
    errors.push('Please select a role to assign');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const groupUsersByCurrentRole = (
  users: User[]
): Record<string, User[]> => {
  return users.reduce(
    (groups, user) => {
      const roleName = getUserRoleName(user);
      if (!groups[roleName]) {
        groups[roleName] = [];
      }
      groups[roleName].push(user);
      return groups;
    },
    {} as Record<string, User[]>
  );
};

// Statistics helpers
export const calculatePermissionChanges = (
  users: User[],
  newRole: Role
): {
  totalChanges: number;
  permissionsAdded: number;
  permissionsRemoved: number;
} => {
  let totalChanges = 0;
  let permissionsAdded = 0;
  let permissionsRemoved = 0;

  users.forEach((user: any) => {
    const raw = user.permissions || [];
    const currentPermissions: Permission[] = Array.isArray(raw)
      ? raw
      : Object.entries(raw).map(([resource, actions]) => ({
          resource,
          actions: actions as string[],
          _id: `${resource}-${(actions as string[]).join('-')}`,
        }));
    const newPermissions = newRole.permissions || [];

    const comparison = comparePermissions(currentPermissions, newPermissions);

    totalChanges += comparison.added.length + comparison.removed.length;
    permissionsAdded += comparison.added.length;
    permissionsRemoved += comparison.removed.length;
  });

  return {
    totalChanges,
    permissionsAdded,
    permissionsRemoved,
  };
};

// Color scheme utilities for consistent UI
export const getRoleColorScheme = (
  roleName: string
): {
  bg: string;
  text: string;
  border: string;
} => {
  const colorSchemes = {
    superadmin: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
    },
    admin: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
    manager: {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
    },
    staff: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    viewer: {
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      border: 'border-gray-200',
    },
  };

  const key = roleName.toLowerCase() as keyof typeof colorSchemes;
  return colorSchemes[key] || colorSchemes.viewer;
};

// Format helpers
export const formatPermissionSummary = (permissions: Permission[]): string => {
  const resourceCount = permissions.length;
  const actionCount = permissions.reduce(
    (count, p) => count + p.actions.length,
    0
  );

  if (resourceCount === 0) return 'No permissions';
  if (resourceCount === 1)
    return `${actionCount} action${actionCount !== 1 ? 's' : ''} on 1 resource`;

  return `${actionCount} actions across ${resourceCount} resources`;
};

export const formatUserSelectionSummary = (users: User[]): string => {
  if (users.length === 0) return 'No users selected';
  if (users.length === 1) return `1 user selected: ${users[0].username}`;
  return `${users.length} users selected`;
};

// Search and filter helpers
export const filterUsersBySearch = (
  users: User[],
  searchTerm: string
): User[] => {
  if (!searchTerm.trim()) return users;

  const term = searchTerm.toLowerCase();
  return users.filter(
    user =>
      user.username.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      getUserRoleName(user).toLowerCase().includes(term)
  );
};

export const sortUsersByName = (users: User[]): User[] => {
  return [...users].sort((a, b) => a.username.localeCompare(b.username));
};

// Error message helpers
export const getBulkUpdateErrorMessage = (
  error: any,
  defaultMessage: string = 'Failed to update user permissions'
): string => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.error?.message) return error.error.message;
  return defaultMessage;
};

// Success message helpers
export const getBulkUpdateSuccessMessage = (
  successCount: number,
  totalCount: number,
  roleName: string
): string => {
  if (successCount === totalCount) {
    return `Successfully updated ${successCount} user${successCount !== 1 ? 's' : ''} to ${roleName} role`;
  } else {
    return `Updated ${successCount} of ${totalCount} users to ${roleName} role. Check individual results for details.`;
  }
};

// Accessibility helpers
export const getAccessibilityLabel = (
  action: string,
  resource: string
): string => {
  const actionLabel = PERMISSION_ACTION_LABELS[action] || action;
  const resourceLabel = PERMISSION_RESOURCE_LABELS[resource] || resource;
  return `${actionLabel} permission for ${resourceLabel}`;
};

// Performance helpers
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const memoizeByKey = <T, R>(
  fn: (key: string, ...args: T[]) => R,
  cacheSize: number = 100
): ((key: string, ...args: T[]) => R) => {
  const cache = new Map<string, R>();

  return (key: string, ...args: T[]) => {
    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = fn(key, ...args);

    if (cache.size >= cacheSize) {
      const firstKey = cache.keys().next().value as string | undefined;
      if (firstKey !== undefined) {
        cache.delete(firstKey);
      }
    }

    cache.set(key, result);
    return result;
  };
};
